import { Container, Graphics, Text, TextStyle } from '../../../vendor/pixi.min.mjs';

const PAD = 10;
const GAP = 6;
const RADIUS = 14;
// Tuned so the adventure grid (37 × 16) lands on px=3 per game cell:
//   floor(120 / 37) = 3  → each board renders 111 × 48 px
//   total minimap size ≈ 248 × 122 (just slightly taller than the 110-px
//   top bar so it visually "punches through" with a clear silhouette).
const CELL_TARGET_W = 120;

// Palette — picks up the colours actually used elsewhere in the game:
//   • panel bg  = purple `PÁLYA` chip colour (semantic — this widget IS the
//                 board indicator)
//   • stroke    = deep menu-purple
//   • active    = the gameplay-screen blue (`SCREEN_BG_COLOR` in SnakeScene)
//                 so the active board reads as a tiny live preview
//   • visited   = green (completed)
//   • unvisited = darker tint of the panel purple
const BG_COLOR = 0xa855f7;
const BG_ALPHA = 0.95;
const DIM_COLOR = 0x6b21a8;
const ACTIVE_BORDER = 0xfbbf24;
const VISITED_COLOR = 0x16a34a;
const UNVISITED_COLOR = 0x6b21a8;
const GRID_BG = 0x004f93;
const BORDER_COLOR = 0x3b1670;

const SNAKE_HEAD_COLOR = 0x4ade80;
const SNAKE_BODY_COLOR = 0x22c55e;
const FOOD_COLOR = 0xfbbf24;
const OBSTACLE_COLOR = 0x4a2410;
const PERK_COLOR = 0xfb923c;
const DOOR_COLOR = 0xfbbf24;
const HOUSE_CLOSED_COLOR = 0xef4444;
const HOUSE_OPEN_COLOR = 0x22c55e;

const NUM_STYLE = new TextStyle({
    fontFamily: 'Poppins, Arial, sans-serif',
    fontSize: 14,
    fontWeight: '800',
    fill: '#ffffff',
});

/**
 * Live minimap for Snake Adventure mode.
 * Shows a 2x2 grid of screens with real-time rendering
 * of the snake, obstacles, food, door, and house on the active screen.
 *
 * Layout:
 *   3  4
 *   1  2
 */
export class SnakeMinimap {
    container = new Container();

    _cells = [];
    _bg = null;
    _currentScreen = 0;
    _visitedScreens = new Set([0]);

    _gridCols = 0;
    _gridRows = 0;
    _pxPerCol = 1;
    _pxPerRow = 1;
    _cellW = CELL_TARGET_W;
    _cellH = CELL_TARGET_W;

    constructor(gridCols, gridRows) {
        this._gridCols = gridCols;
        this._gridRows = gridRows;

        this._pxPerCol = Math.max(2, Math.floor(CELL_TARGET_W / gridCols));
        this._pxPerRow = Math.max(2, Math.floor(CELL_TARGET_W / gridRows));
        const px = Math.min(this._pxPerCol, this._pxPerRow);
        this._pxPerCol = px;
        this._pxPerRow = px;

        this._cellW = gridCols * px;
        this._cellH = gridRows * px;

        this._build();
    }

    _build() {
        const totalW = PAD * 2 + this._cellW * 2 + GAP;
        const totalH = PAD * 2 + this._cellH * 2 + GAP;

        this._bg = new Graphics();
        this._bg.roundRect(0, 0, totalW, totalH, RADIUS);
        this._bg.fill({ color: BG_COLOR, alpha: BG_ALPHA });
        this._bg.stroke({ color: BORDER_COLOR, width: 1.5 });
        this.container.addChild(this._bg);

        const layout = [
            { screen: 2, gCol: 0, gRow: 0 },
            { screen: 3, gCol: 1, gRow: 0 },
            { screen: 0, gCol: 0, gRow: 1 },
            { screen: 1, gCol: 1, gRow: 1 },
        ];

        for (const { screen, gCol, gRow } of layout) {
            const x = PAD + gCol * (this._cellW + GAP);
            const y = PAD + gRow * (this._cellH + GAP);

            const board = new Graphics();
            this.container.addChild(board);

            const overlay = new Graphics();
            this.container.addChild(overlay);

            const label = new Text({ text: String(screen + 1), style: NUM_STYLE });
            label.anchor.set(1, 0);
            label.x = x + this._cellW - 2;
            label.y = y + 1;
            label.alpha = 0.5;
            this.container.addChild(label);

            this._cells.push({ screen, x, y, board, overlay, label });
        }
    }

    /**
     * Full update called each frame from SnakeScene.
     */
    renderLive({ currentScreen, snake, obstacles, food, perks, exit, doorUnlocked, housePos, houseOpen }) {
        this._currentScreen = currentScreen;
        this._visitedScreens.add(currentScreen);

        const px = this._pxPerCol;

        for (const cell of this._cells) {
            const { screen, x, y, board, overlay, label } = cell;
            const isActive = screen === currentScreen;
            const visited = this._visitedScreens.has(screen);

            board.clear();
            overlay.clear();

            board.roundRect(x, y, this._cellW, this._cellH, 3);
            board.fill(isActive ? GRID_BG : (visited ? VISITED_COLOR : UNVISITED_COLOR));

            if (isActive) {
                if (obstacles?._items) {
                    for (const obs of obstacles._items) {
                        board.rect(x + obs.col * px, y + obs.row * px, obs.w * px, obs.h * px);
                        board.fill(OBSTACLE_COLOR);
                    }
                }

                if (housePos) {
                    const hw = 2 * px;
                    const hh = 2 * px;
                    board.rect(x + housePos.col * px, y + housePos.row * px, hw, hh);
                    board.fill(houseOpen ? HOUSE_OPEN_COLOR : HOUSE_CLOSED_COLOR);
                }

                if (food) {
                    board.rect(x + food.col * px, y + food.row * px, px, px);
                    board.fill(FOOD_COLOR);
                }

                if (perks?._perks) {
                    for (const p of perks._perks) {
                        board.rect(x + p.col * px, y + p.row * px, px, px);
                        board.fill(PERK_COLOR);
                    }
                }

                if (exit && doorUnlocked) {
                    const e = exit;
                    switch (e.side) {
                        case 'right':
                            board.rect(x + this._cellW - px, y + e.start * px, px, e.width * px);
                            board.fill(DOOR_COLOR); break;
                        case 'left':
                            board.rect(x, y + e.start * px, px, e.width * px);
                            board.fill(DOOR_COLOR); break;
                        case 'top':
                            board.rect(x + e.start * px, y, e.width * px, px);
                            board.fill(DOOR_COLOR); break;
                        case 'bottom':
                            board.rect(x + e.start * px, y + this._cellH - px, e.width * px, px);
                            board.fill(DOOR_COLOR); break;
                    }
                }

                if (snake?.segments) {
                    for (let i = snake.segments.length - 1; i >= 1; i--) {
                        const seg = snake.segments[i];
                        board.rect(x + seg.col * px, y + seg.row * px, px, px);
                        board.fill(SNAKE_BODY_COLOR);
                    }
                    if (snake.segments.length > 0) {
                        const head = snake.segments[0];
                        board.rect(x + head.col * px, y + head.row * px, px, px);
                        board.fill(SNAKE_HEAD_COLOR);
                    }
                }

                overlay.roundRect(x - 1, y - 1, this._cellW + 2, this._cellH + 2, 4);
                overlay.stroke({ color: ACTIVE_BORDER, width: 2 });

                label.alpha = 0.7;
            } else if (visited) {
                const cx = x + this._cellW / 2;
                const cy = y + this._cellH / 2;
                overlay.moveTo(cx - 6, cy);
                overlay.lineTo(cx - 2, cy + 5);
                overlay.lineTo(cx + 7, cy - 5);
                overlay.stroke({ color: 0x4ade80, width: 2 });
                label.alpha = 0.4;
            } else {
                label.alpha = 0.25;
            }
        }
    }

    /**
     * Simple highlight update (no live data).
     */
    update(currentScreen) {
        this._currentScreen = currentScreen;
        this._visitedScreens = new Set([currentScreen]);
        for (const cell of this._cells) {
            cell.board.clear();
            cell.overlay.clear();
            const isActive = cell.screen === currentScreen;
            cell.board.roundRect(cell.x, cell.y, this._cellW, this._cellH, 3);
            cell.board.fill(isActive ? DIM_COLOR : UNVISITED_COLOR);
            if (isActive) {
                cell.overlay.roundRect(cell.x - 1, cell.y - 1, this._cellW + 2, this._cellH + 2, 4);
                cell.overlay.stroke({ color: ACTIVE_BORDER, width: 2 });
            }
        }
    }

    destroy() {
        this.container.destroy({ children: true });
    }
}
